// =====================================================================
// PDF « Liste de départ » (design officiel) — partagé par la page Liste
// de départ et la page Chrono (partage WhatsApp), 07.10.2026 : un seul
// rendu partout. Tri impression (config.printSort) appliqué si `sorted`.
// À placer dans src/utils/.
// =====================================================================
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { groupBySections } from "./printSort";
import { brandHeader, brandFooter, brandBottomMargin } from "./pdfBranding";

const DISCIPLINE_LABELS = {
    SL: "Slalom",
    GS: "Riesenslalom",
    SG: "Super-G",
    DH: "Downhill / Abfahrt",
    CR: "Combi-Race",
    AC: "Alpine-Kombi",
    PS: "Parallel-Slalom",
};

// Retourne un File (et le télécharge si save = true).
export function officialStartlistPdf({ config, eventKey, runNumber, startlist, includeRaceInfo = true, sorted = true, save = true }) {

    const cfg = config || {};
    const doc = new jsPDF();

    // En-tête (titre, discipline, manche) — répété en haut des pages
    // de catégorie quand il y a un saut de page par catégorie.
    const drawTitle = () => {
        doc.setFontSize(13);
        doc.setFont(undefined, "bold");
        doc.text(cfg.raceTitle || cfg.event || eventKey, 105, 14, { align: "center" });
        doc.setFontSize(11);
        doc.text(DISCIPLINE_LABELS[cfg.discipline] || cfg.discipline || "", 105, 21, { align: "center" });
        doc.setFontSize(10);
        doc.text(`Start list for run ${runNumber}`, 105, 27, { align: "center" });
        doc.setFont(undefined, "normal");
        doc.setFontSize(8);
        const left = [cfg.place, cfg.date].filter(Boolean).join(", ");
        if (left) doc.text(left, 14, 31);
        if (cfg.codex) doc.text(`Codex ${cfg.codex}`, 196, 31, { align: "right" });
        return 36;
    };

    let y = drawTitle();

    if (includeRaceInfo) {
        autoTable(doc, {
            startY: y,
            margin: { left: 14, right: 108 },
            theme: "plain",
            styles: { fontSize: 8, cellPadding: 1 },
            head: [["COMPETITION JURY", ""]],
            body: [
                ["TECHNICAL DELEGATE", `${cfg.jury?.td || "—"} (${cfg.jury?.tdNation || "—"})`],
                ["CHIEF OF RACE", `${cfg.jury?.chief || "—"} (${cfg.jury?.chiefNation || "—"})`],
                ["REFEREE", `${cfg.jury?.referee || "—"} (${cfg.jury?.refereeNation || "—"})`],
            ],
        });
        y = doc.lastAutoTable.finalY + 4;

        // Parcours des deux manches côte à côte (le modèle VOLA les
        // affiche toutes les deux même sur un "Start list for run 1").
        const courseBody = (runConfig) => {
            const forerunners = (runConfig?.forerunners || [])
                .filter((f) => f.name)
                .map((f) => `${f.name}${f.nation ? " (" + f.nation + ")" : ""}`)
                .join("\n");
            return [
                ["COURSESETTER", `${runConfig?.courseSetter || "—"} (${runConfig?.courseSetterNation || "—"})`],
                ["FORERUNNERS", forerunners || "—"],
                ["COURSE", cfg.technical?.courseName || "—"],
                ["START", cfg.technical?.startAltitude ? `${cfg.technical.startAltitude} m` : "—"],
                ["FINISH", cfg.technical?.finishAltitude ? `${cfg.technical.finishAltitude} m` : "—"],
                ["HOMOLOGATION", cfg.technical?.homologation || "—"],
                ["GATES", runConfig?.gates || "—"],
                ["TURNING GATES", runConfig?.turningGates || "—"],
                ["STARTING TIME", runConfig?.startTime || "—"],
            ];
        };
        const runsToShow = (cfg.runs || []).length > 1 ? [0, 1] : [0];
        if (runsToShow.length === 1) {
            autoTable(doc, {
                startY: y,
                margin: { left: 14, right: 14, bottom: brandBottomMargin(cfg) },
                theme: "plain",
                styles: { fontSize: 8, cellPadding: 1 },
                head: [[`RUN ${runsToShow[0] + 1}`, ""]],
                body: courseBody(cfg.runs?.[runsToShow[0]]),
            });
            y = doc.lastAutoTable.finalY + 4;
        } else {
            autoTable(doc, {
                startY: y,
                margin: { left: 14, right: 108 },
                theme: "plain",
                styles: { fontSize: 8, cellPadding: 1 },
                head: [["RUN 1", ""]],
                body: courseBody(cfg.runs?.[0]),
            });
            const y1 = doc.lastAutoTable.finalY;
            autoTable(doc, {
                startY: y,
                margin: { left: 108, right: 14 },
                theme: "plain",
                styles: { fontSize: 8, cellPadding: 1 },
                head: [["RUN 2", ""]],
                body: courseBody(cfg.runs?.[1]),
            });
            y = Math.max(y1, doc.lastAutoTable.finalY) + 4;
        }
    }

    doc.setFontSize(8);
    doc.text(`LIST n°${cfg.plist || cfg.usedFisList || "—"}`, 14, y);
    const nations = new Set(startlist.map((a) => a.nation).filter(Boolean));
    doc.text(`Number of competitors: ${startlist.length}, Number of NSA: ${nations.size}`, 14, y + 5);
    y += 10;

    const head = [["Order", "Bib", "Code", "Name", "Year", "Nation", "Club", "List points"]];
    const rowOf = (a, i) => {
        const p = parseFloat(a.points);
        return [
            a.order ?? i + 1,
            a.bib ?? "",
            a.fiscode ?? "",
            `${a.lastname || ""} ${a.firstname || ""}`.trim(),
            a.birthyear ?? "",
            a.nation ?? "",
            a.club ?? "",
            isNaN(p) ? "-,--" : p.toFixed(2).replace(".", ","),
        ];
    };

    const groups = groupBySections(startlist, sorted ? cfg.printSort : null, cfg.categories);
    groups.forEach((g, gi) => {
        if (g.category) {
            if (cfg.printSort?.pageBreak && gi > 0) {
                doc.addPage();
                y = drawTitle();
            } else if (y > doc.internal.pageSize.getHeight() - 40) {
                doc.addPage();
                y = 20;
            }
            doc.setFontSize(11);
            doc.setFont(undefined, "bold");
            doc.text(`${g.category} (${g.entries.length})`, 14, y);
            doc.setFont(undefined, "normal");
            y += 2;
        }
        autoTable(doc, {
            startY: y,
            margin: { left: 14, right: 14, bottom: brandBottomMargin(cfg) },
            styles: { fontSize: 8, textColor: 0 },
            headStyles: { fillColor: [20, 20, 20], textColor: 255 },
            head,
            body: g.entries.map((a) => rowOf(a, startlist.indexOf(a))),
        });
        y = doc.lastAutoTable.finalY + 8;
    });

    // En-tête (logos) sur chaque page + pied de page commun (bandeau,
    // Timing / logo 321-timing / Data processing, date de création, pages).
    for (let i = 1; i <= doc.getNumberOfPages(); i++) {
        doc.setPage(i);
        brandHeader(doc, cfg);
    }
    brandFooter(doc, cfg);

    const filename = `startlist-manche${runNumber}-${eventKey}.pdf`;
    if (save) doc.save(filename);
    return new File([doc.output("blob")], filename, { type: "application/pdf" });
}